// Teachers list (SPEC §7.5): name, courses, current own groups, contacts; filter; show archived.

import type { ApiResult } from "../../api/client";
import { fetchGroups } from "../../api/groups";
import type { Group, Teacher } from "../../api/types";
import { currentLocale, t } from "../../i18n/i18n";
import { navigate, setRedraw, type RouteMatch } from "../../router";
import { isAdmin } from "../../session";
import { matchesSearch, sortByName } from "../../text";
import { createButton, createElement, createLink } from "../../ui/dom";
import { createCheckbox, createInputField, type Field } from "../../ui/forms";
import { buildContacts } from "../lesson/candidatesTable";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { courseName, loadReferenceData, type ReferenceData } from "../referenceData";

interface TeachersView {
  reference: ReferenceData;
  groups: Group[];
  includeArchived: boolean;
}

export async function renderTeachersListPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const page: HTMLElement = startPage(container, t("teachers.title"));
  page.append(createLoading());
  const reference: ApiResult<ReferenceData> = await loadReferenceData();
  const groups: ApiResult<Group[]> = await fetchGroups(false);
  if (reference.ok === false) {
    showPageProblem(page, reference.problem);
    return;
  }

  if (groups.ok === false) {
    showPageProblem(page, groups.problem);
    return;
  }

  const view: TeachersView = {
    reference: reference.data, groups: groups.data, includeArchived: route.query.get("archived") === "1",
  };
  const draw = (): void => {
    drawList(startPage(container, t("teachers.title")), view);
  };
  setRedraw(container, draw);
  draw();
}

function drawList(page: HTMLElement, view: TeachersView): void {
  const search: Field<HTMLInputElement> = createInputField(t("app.search"), "search", "");
  const archived: Field<HTMLInputElement> = createCheckbox(t("app.showArchived"), view.includeArchived);
  archived.input.addEventListener("change", (): void => {
    navigate(archived.input.checked ? "/teachers?archived=1" : "/teachers");
  });
  const toolbar: HTMLDivElement = createElement("div", "toolbar");
  toolbar.append(search.wrapper, archived.wrapper);
  if (isAdmin()) {
    toolbar.append(createButton(t("teachers.newTeacher"), (): void => {
      navigate("/teachers/new");
    }, "button-primary"));
  }

  const tableArea: HTMLDivElement = createElement("div", "table-scroll");
  const refresh = (): void => {
    tableArea.replaceChildren(buildTable(view, search.input.value));
  };
  search.input.addEventListener("input", refresh);
  page.append(toolbar, tableArea);
  refresh();
}

function buildTable(view: TeachersView, filter: string): HTMLElement {
  const teachers: Teacher[] = view.reference.teachers
    .filter((teacher: Teacher): boolean => view.includeArchived || teacher.archived === false)
    .filter((teacher: Teacher): boolean => matchesSearch(`${teacher.firstName} ${teacher.lastName}`, filter));
  if (teachers.length === 0) {
    return createElement("p", "muted", t("teachers.noTeachers"));
  }

  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("app.name"), t("teachers.courses"), t("teachers.ownGroups"), t("teachers.contacts")]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const teacher of sortByName(teachers, currentLocale())) {
    body.append(buildRow(teacher, view));
  }

  return table;
}

function buildRow(teacher: Teacher, view: TeachersView): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  const name: HTMLTableCellElement = createElement("td");
  name.append(createLink(`/teachers/${teacher.id}`, `${teacher.lastName} ${teacher.firstName}`));
  if (teacher.archived) {
    name.append(" ", createElement("span", "badge badge-neutral", t("app.archived")));
  }

  const courses: string = teacher.courseIds.map((id: string): string => courseName(view.reference, id)).join(", ");
  const ownGroups: string = view.groups
    .filter((group: Group): boolean => group.currentPeriod.teacherId === teacher.id)
    .map((group: Group): string => group.name)
    .join(", ");
  const contacts: HTMLTableCellElement = createElement("td");
  contacts.append(buildContacts(teacher));
  row.append(name, createElement("td", "", courses), createElement("td", "", ownGroups), contacts);
  return row;
}
